import React, {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAccessibleTeams } from './AccessibleTeamsContext';

export interface CurrentTeamContextValue {
  currentTeamId: string | null;
  setCurrentTeam: (teamId: string) => void;
  isLoading: boolean;
}

const CurrentTeamContext = createContext<CurrentTeamContextValue | undefined>(
  undefined,
);

interface CurrentTeamProviderProps {
  children: ReactNode;
}

const SELECTED_TEAM_KEY = 'platform-selected-team';

export const CurrentTeamProvider: React.FC<CurrentTeamProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const { teams, isLoading: teamsLoading } = useAccessibleTeams();
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);

  // Sync current team with URL parameter
  useEffect(() => {
    if (teamId && teams.some((team) => team.teamId === teamId)) {
      setCurrentTeamId(teamId);
      // Save to localStorage when team is selected
      localStorage.setItem(SELECTED_TEAM_KEY, teamId);
    }
  }, [teamId, teams]);

  const setCurrentTeam = (newTeamId: string) => {
    // Verify user has access to this team
    if (!teams.some((team) => team.teamId === newTeamId)) {
      // User does not have access to this team
      return;
    }

    setCurrentTeamId(newTeamId);
    // Navigate to the new team's workspace
    void navigate(`/team/${newTeamId}`);
  };

  const value: CurrentTeamContextValue = {
    currentTeamId,
    setCurrentTeam,
    isLoading: teamsLoading,
  };

  return (
    <CurrentTeamContext.Provider value={value}>
      {children}
    </CurrentTeamContext.Provider>
  );
};

export const useCurrentTeam = (): CurrentTeamContextValue => {
  const context = useContext(CurrentTeamContext);
  if (!context) {
    throw new Error('useCurrentTeam must be used within a CurrentTeamProvider');
  }
  return context;
};
